import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import useAuth from "../../../../Hooks/useAuth";
import useFirmScopeSelection, { toScopeFirmOption } from "../../../../Hooks/useFirmScopeSelection";
import usePermission from "../../../../Hooks/usePermission";
import {
  useDeleteBillMutation,
  useUpdateGiveFirmProductQuotaMutation,
  useUpdateTakeBackFirmProductQuotaMutation,
} from "../../../../store";
import QuotaOperationDrawer from "../QuotaOperationDrawer";
import ConfirmationModal from "../ConfirmationModal";
import BillDetailDrawer from "../BillDetailDrawer";

import CustomPagination from "../CustomPagination";
import {
  FaPlus,
  FaMinus,
  FaFileInvoice,
  FaTrashAlt,
} from "react-icons/fa";
import { Alert, Snackbar } from "@mui/material";
import { MascotLoadingOverlay } from "../../../shared";
import { useLazyGetBillsQuery } from "../../../../store/storageRoles/billsApi";
import { FirmDropdown } from "../../../atoms";
import { useLazyGetFirmProductsQuery } from "../../../../store/storageRoles/firmApi";

import FormatDateHourString from "../../../Utils/FormatDateHourString";
import { useDebounce } from "../../../../Hooks/useDebounce";
import { getBillTypeDetails } from "../../../../utils/billTypeDetails";

const quotaColors = [
  { bg: "bg-blue-50", text: "text-blue-700", label: "text-blue-500", dot: "bg-blue-400" },
  { bg: "bg-emerald-50", text: "text-emerald-700", label: "text-emerald-500", dot: "bg-emerald-400" },
  { bg: "bg-amber-50", text: "text-amber-700", label: "text-amber-500", dot: "bg-amber-400" },
  { bg: "bg-indigo-50", text: "text-indigo-700", label: "text-indigo-500", dot: "bg-indigo-400" },
  { bg: "bg-pink-50", text: "text-pink-700", label: "text-pink-500", dot: "bg-pink-400" },
  { bg: "bg-purple-50", text: "text-purple-700", label: "text-purple-500", dot: "bg-purple-400" },
];

const BillsTab = ({ firm, readOnly = false }) => {
  const { auth } = useAuth();
  const { hasPermission } = usePermission();
  const id = firm?._id;
  const isDealer = auth?.role === "dealer";

  // State'ler
  const [pagination, setPagination] = useState(1);
  const [searchBillNo, setSearchBillNo] = useState("");
  const [searchProductName, setSearchProductName] = useState("");
  // Alt firma kapsamı — VARSAYILAN: firma + TÜM alt firmaları (kullanıcı
  // kararı 2026-09-14). Ayrıntı: hooks/useFirmScopeSelection.
  const [firmOptions, setFirmOptions] = useState([]);
  const {
    requestFirmIds,
    selectedFirms,
    onChange: onScopeChange,
    hasSubFirms,
  } = useFirmScopeSelection(firmOptions);
  const [modal, setModal] = useState({ open: false, type: "" });
  // Kota işleminin (bedelsiz yükleme / iade) yapılacağı firma. Birden fazla
  // firma seçiliyken drawer'dan değiştirilir; varsayılan firmanın kendisi.
  const [quotaTargetFirmId, setQuotaTargetFirmId] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ open: false, bill: null });
  // Silme modalındaki "firma kotası etkilenmesin" seçeneği
  // (bills.deleteWithoutQuota izni gerektirir)
  const [skipQuotaEffect, setSkipQuotaEffect] = useState(false);
  // Satıra tıklayınca açılan fatura detay drawer'ı
  const [detailDrawer, setDetailDrawer] = useState({ open: false, bill: null });
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // İsteğe gidecek kapsam — BOŞ dizi "tüm ağaç" demektir (sunucu sözleşmesi).
  // NOT: aşağıdaki başarı efektleri (kota yükle/iade/sil) bağımlılık
  // listelerinde kapsamı taşımıyor; ref üzerinden okunmazsa bayat closure
  // yüzünden yeniden çekim yanlış kapsama düşer ve kartlar/liste seçili
  // kapsamdan çıkardı.
  const effectiveFirmIds = requestFirmIds;

  const firmScopeRef = useRef(effectiveFirmIds);
  useEffect(() => {
    firmScopeRef.current = effectiveFirmIds;
  }, [effectiveFirmIds]);

  // RTK Query Hooks
  const [
    getBills,
    { data: billsResponse, isLoading: billsLoading, isFetching: billsFetching },
  ] = useLazyGetBillsQuery();
  const [
    getProducts,
    {
      data: productsResponse,
      isLoading: productsLoading,
      // Her SORGUDA skeleton: firma seçimi değişince kartlar eski toplamı
      // gösterip sonra zıplıyordu; isFetching ilk yüklemeyi de kapsar.
      isFetching: productsFetching,
    },
  ] = useLazyGetFirmProductsQuery();
  // KOTA DRAWER'LARI İÇİN AYRI SORGU: kartlardaki toplam seçili firmaların
  // toplamıdır, ama "Bedelsiz Kota Yükle" / "İade Kota Al" işlemi HER ZAMAN
  // bu firmanın havuzuna gider. Drawer'a birleşik toplamı verirsek kullanıcı
  // firmada olmayan bir miktarı iade almaya kalkar ve sunucu reddeder.
  const [getOwnProducts, { data: ownProductsResponse }] =
    useLazyGetFirmProductsQuery();

  const [
    deleteBill,
    {
      isLoading: deleteLoading,
      isSuccess: deleteSuccess,
      isError: deleteIsError,
      error: deleteError,
      reset: resetDelete,
    },
  ] = useDeleteBillMutation();
  const [
    giveQuota,
    {
      isLoading: giveLoading,
      isSuccess: giveSuccess,
      isError: giveIsError,
      error: giveError,
      reset: resetGive,
    },
  ] = useUpdateGiveFirmProductQuotaMutation();
  const [
    takebackQuota,
    {
      isLoading: takebackLoading,
      isSuccess: takebackSuccess,
      isError: takebackIsError,
      error: takebackError,
      reset: resetTakeback,
    },
  ] = useUpdateTakeBackFirmProductQuotaMutation();

  // ARAMA DEBOUNCE — her tuş vuruşunda sunucuya istek gitmesin.
  const debouncedBillNo = useDebounce(searchBillNo, 400);
  const debouncedProductName = useDebounce(searchProductName, 400);

  // Sayfa başına GRUP sayısı (sunucu da bu değeri kullanır).
  const pageSize = 100;
  // SAYFA GEÇİŞİ DEBOUNCE'LU: pager'daki aktif sayfa anında güncellenir
  // (tıklama tepkisiz hissettirmesin) ama istek 200 ms bekler — ">>" ile
  // hızlı geçilirken aradaki her sayfa için sorgu atılmaz.
  const debouncedPagination = useDebounce(pagination, 200);

  // Veriyi manuel olarak çekmek için useEffect (bayi rolünde ürün listesi istenmez, sadece fatura listesi)
  useEffect(() => {
    if (id && auth.accessToken) {
      // SAYFALAMA VE ARAMA SUNUCUDA (2026-09-01): tüm faturalar tek istekte
      // inmiyor; yalnız o sayfanın grupları geliyor. Sayfa numarası
      // debounce'lu — ">>" ile hızlı geçişte her adım için istek atılmasın.
      getBills({
        id,
        token: auth.accessToken,
        firmIds: effectiveFirmIds,
        page: debouncedPagination,
        pageSize,
        billNo: debouncedBillNo || "",
        productName: debouncedProductName || "",
      });
    }
  }, [
    id,
    auth.accessToken,
    getBills,
    effectiveFirmIds,
    debouncedPagination,
    debouncedBillNo,
    debouncedProductName,
  ]);

  // Ürün kota kartları seçili firmaları kapsar — yalnız KAPSAM değişince
  // çekilir. Önceden fatura listesiyle aynı efektteydi; her sayfa / arama
  // değişiminde tüm cihazları okuyan ağır getFirmProducts yeniden koşuyordu
  // (#405). Kota işlemleri sonrası yenileme aşağıdaki başarı efektlerinde.
  useEffect(() => {
    if (id && auth.accessToken && !isDealer) {
      getProducts({ id, token: auth.accessToken, firmIds: effectiveFirmIds });
    }
  }, [id, auth.accessToken, isDealer, getProducts, effectiveFirmIds]);

  // Kota drawer'ının bakiyeleri: TEK firmanın kotası (kapsam toplamı değil —
  // işlem her zaman tek bir firmanın havuzuna gider). Hedef firma drawer'dan
  // değiştirilince yalnız bu sorgu yenilenir; fatura listesi boşuna
  // çekilmez.
  useEffect(() => {
    const targetId = quotaTargetFirmId || id;
    if (targetId && auth.accessToken && !isDealer) {
      getOwnProducts({ id: targetId, token: auth.accessToken });
    }
  }, [quotaTargetFirmId, id, auth.accessToken, isDealer, getOwnProducts]);

  const billsData = useMemo(
    () => billsResponse?.data?.bills || [],
    [billsResponse]
  );
  // Sayfalama sunucudan: toplam GRUP sayısı (panelin gösterdiği satır sayısı).
  const totalGroupCount = billsResponse?.data?.total ?? 0;
  const productDatas = useMemo(
    () => productsResponse?.data || [],
    [productsResponse]
  );
  // Kota işlemleri (yükle/iade) yalnız bu firmanın havuzunu değiştirir.
  const ownProductDatas = useMemo(
    () => ownProductsResponse?.data || productsResponse?.data || [],
    [ownProductsResponse, productsResponse]
  );

  // Kota kartları: toplam = seçili firmalar; ana firma payı ayrı sorgudan
  // geliyor, alt firmalar payı ikisinin FARKI. Böylece kart tek bakışta
  // "bu firmada ne var / altında ne var" sorusunu cevaplıyor.
  const quotaCards = useMemo(() => {
    const ownByName = new Map(
      (ownProductDatas || []).map((p) => [p.productName, Number(p.quota) || 0])
    );
    return (productDatas || []).map((p) => {
      const total = Number(p.quota) || 0;
      const own = ownByName.get(p.productName) || 0;
      return {
        productName: p.productName,
        total,
        own,
        // Negatife düşmesin: iki sorgu arasında kota hareketi olabilir.
        sub: Math.max(total - own, 0),
      };
    });
  }, [productDatas, ownProductDatas]);

  // Seçici seçenekleri — sunucudan gelen kök + alt firma listesi. Kapsam
  // daraltılsa bile sunucu TÜM izinli firmaları döndürür; son bilinen liste
  // state'te tutulur ki yanıt arasında seçici boşalmasın.
  const serverFirms = billsResponse?.data?.firms;
  useEffect(() => {
    if (!Array.isArray(serverFirms) || serverFirms.length === 0) return;
    setFirmOptions(
      serverFirms.map(toScopeFirmOption),
    );
  }, [serverFirms]);
  // Birden fazla firma seçiliyse tabloda "İşlem Firması" sütunu açılır;
  // tek firmada o sütun hiç basılmaz (gereksiz yer kaplamasın).
  // Kapsam birden fazla firmayı içeriyorsa tabloda "İşlem Firması" sütunu
  // açılır. `effectiveFirmIds` "tümü" hâlinde BOŞ olduğu için uzunluğuna
  // bakılmaz — işaretli firma sayısına bakılır.
  const showFirmColumn = selectedFirms.length > 1;

  // Yüklenme durumları
  const isProcessing = deleteLoading || giveLoading || takebackLoading;

  // Filtreleme ve Arama Mantığı — SATIR (fatura kalemi) seviyesinde
  const filteredAndSortedBills = useMemo(() => {
    let filtered = billsData;
    if (debouncedBillNo) {
      const term = debouncedBillNo.toLowerCase();
      filtered = filtered.filter((bill) =>
        bill.billNo?.toLowerCase().includes(term)
      );
    }
    if (debouncedProductName) {
      const term = debouncedProductName.toLowerCase();
      // Ürün araması artık ÜRÜN TİPİNDE de arar — liste ürün tipini
      // gösterdiği için kullanıcının gördüğü metinle arama tutarlı olmalı.
      filtered = filtered.filter(
        (bill) =>
          bill.productName?.toLowerCase().includes(term) ||
          bill.productTypeName?.toLowerCase().includes(term)
      );
    }
    return [...filtered].sort(
      (a, b) => new Date(b.createdInfo) - new Date(a.createdInfo)
    );
  }, [billsData, debouncedBillNo, debouncedProductName]);

  // FATURA GRUPLAMA (kullanıcı isteği): aynı faturaya ait alt alta duran
  // kalemler tek satırda toplanır. Grup anahtarı billNo + billType —
  // billType de gerekli çünkü aynı fatura no hem "Bill" hem "GetReturn"
  // olarak görünebilir ve bunlar ZIT işlemlerdir, toplanmamalıdır.
  //
  // Grup içinde miktarlar ÜRÜN TİPİNE göre toplanır: bir faturada altı ayrı
  // "Türk Kahvesi" kalemi varsa listede tek "Türk Kahvesi × 2000" görünür.
  // Kalemlerin kendisi kaybolmaz — satıra tıklanınca drawer'da listelenir.
  const groupedBills = useMemo(() => {
    const map = new Map();
    filteredAndSortedBills.forEach((bill) => {
      // GRUP ANAHTARI: fatura no + islem turu + TARIH (gun).
      //  - billType sart: ayni fatura no hem "Bill" hem "GetReturn" olabilir
      //    ve bunlar ZIT islemlerdir, toplanmamalidir.
      //  - TARIH sart (kullanici istegi): ayni fatura numarasi farkli
      //    tarihlerde tekrar kullanilabiliyor; yalnizca numaraya bakmak
      //    ilgisiz iki faturayi tek satirda birlestirirdi.
      // TAM ZAMAN DAMGASI (2026-08-25 duzeltmesi). Once GUN hassasiyeti
      // kullaniliyordu; ayni fatura numarasinin ayni gun icindeki FARKLI
      // saatlerde girilmis kalemleri yanlislikla tek satirda birlesiyordu.
      // Kullanici karari: gruplanmak icin tarih VE saat de ayni olmali.
      //
      // Excel ice aktarmada bir faturanin tum kalemleri ayni TARIHI degerini
      // tasir (BillPage.js: createdInfo = convertExcelDate(obj["TARIHI"])),
      // bu yuzden tam esitlik gercek faturayi bolmez.
      const timeKey = bill.createdInfo
        ? new Date(bill.createdInfo).getTime()
        : "-";
      // KOTA PAYLASIMI: yon de anahtarin parcasi. Giden ve gelen ZIT
      // islemlerdir (billType ikisinde de "QuotaShare") — ayni saniyede
      // dusen bir giden + bir gelen kayit yoksa bile birlestirmek yanlis
      // olurdu; `billType` tek basina bunlari ayirmiyor.
      const directionKey = bill.quotaShare?.direction || "-";
      const key = `${bill.billNo || "-"}__${bill.billType || "-"}__${directionKey}__${timeKey}`;
      let g = map.get(key);
      if (!g) {
        g = {
          key,
          billNo: bill.billNo,
          billType: bill.billType,
          quotaShare: bill.quotaShare,
          billerName: bill.billerName,
          firmApellation: bill.firmApellation,
          // Faturanın ait olduğu firma (alt firma faturaları da listede).
          firmName: bill.firmName,
          isSubFirmBill: !!bill.isSubFirmBill,
          createdInfo: bill.createdInfo,
          totalIncome: 0,
          products: new Map(), // ürün tipi -> toplam miktar
          lines: [],
        };
        map.set(key, g);
      }
      // Grubun tarihi EN YENİ kalemin tarihi; sıralama zaten yeniden eskiye.
      if (new Date(bill.createdInfo) > new Date(g.createdInfo)) {
        g.createdInfo = bill.createdInfo;
      }
      // TUTAR = income (SATIR TOPLAMI). `price` BIRIM FIYAT'tir
      // (BillPage.js: price = SATIR TOPLAMI / SATIR MIKTARI) ve TOPLANAMAZ —
      // birim fiyatlari toplamak ne birim fiyat ne toplam olan anlamsiz bir
      // sayi uretir. Birim fiyat yalnizca KALEM bazinda gosterilir.
      g.totalIncome += Number(bill.income) || 0;
      // productTypeName yoksa productName'e düşülür — eski kayıtlarda tip boş.
      const typeName = bill.productTypeName || bill.productName || "-";
      g.products.set(
        typeName,
        (g.products.get(typeName) || 0) + (Number(bill.quota) || 0)
      );
      g.lines.push(bill);
    });
    return Array.from(map.values()).map((g) => ({
      ...g,
      productSummary: Array.from(g.products.entries()).map(([name, quota]) => ({
        name,
        quota,
      })),
    }));
  }, [filteredAndSortedBills]);

  // Sunucudan gelen sayfa zaten pageSize kadar GRUP içeriyor; istemcide
  // ayrıca dilimlemeye gerek yok.
  const paginatedBills = groupedBills;

  // Arama metni değiştiğinde sayfayı başa al — DEBOUNCE'LI değere bağlı,
  // yoksa kullanıcı yazarken sayfa sürekli 1'e atlar.
  // Firma seçimi de aynı: kapsam değişince eldeki sayfa numarası geçersiz
  // kalıp boş liste gösterebiliyordu.
  useEffect(() => {
    setPagination(1);
  }, [debouncedBillNo, debouncedProductName, effectiveFirmIds]);

  // Handler'lar
  const showNotification = useCallback((message, severity = "success") => {
    setNotification({ open: true, message, severity });
  }, []);

  const handleNotificationClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setNotification({ ...notification, open: false });
  };

  // İşlem sonuçlarını izle ve bildirim göster
  useEffect(() => {
    if (giveSuccess) {
      showNotification("Kota başarıyla yüklendi.", "success");
      setModal({ open: false, type: "" });
      resetGive();
      getBills({ id, token: auth.accessToken, firmIds: firmScopeRef.current });
      getProducts({ id, token: auth.accessToken, firmIds: firmScopeRef.current });
      getOwnProducts({ id, token: auth.accessToken });
    }
    if (giveIsError) {
      showNotification(
        giveError?.data?.message || "Kota yüklenirken bir hata oluştu.",
        "error"
      );
      resetGive();
    }
  }, [giveSuccess, giveIsError, giveError, resetGive, showNotification]);

  useEffect(() => {
    if (takebackSuccess) {
      showNotification("Kota başarıyla iade alındı.", "success");
      resetTakeback();
      getBills({ id, token: auth.accessToken, firmIds: firmScopeRef.current });
      getProducts({ id, token: auth.accessToken, firmIds: firmScopeRef.current });
      getOwnProducts({ id, token: auth.accessToken });
    }
    if (takebackIsError) {
      showNotification(
        takebackError?.data?.message || "Kota iade alınırken bir hata oluştu.",
        "error"
      );
      resetTakeback();
    }
  }, [
    takebackSuccess,
    takebackIsError,
    takebackError,
    resetTakeback,
    showNotification,
  ]);

  useEffect(() => {
    if (deleteSuccess) {
      showNotification("Fatura başarıyla silindi.", "success");
      resetDelete();
      getBills({ id, token: auth.accessToken, firmIds: firmScopeRef.current });
      getProducts({ id, token: auth.accessToken, firmIds: firmScopeRef.current });
      getOwnProducts({ id, token: auth.accessToken });
    }
    if (deleteIsError) {
      showNotification(
        deleteError?.data?.message || "Fatura silinirken bir hata oluştu.",
        "error"
      );
      resetDelete();
    }
  }, [
    deleteSuccess,
    deleteIsError,
    deleteError,
    resetDelete,
    showNotification,
  ]);

  const handleDeleteRequest = (bill) => {
    // Checkbox her açılışta temiz başlar — önceki seçim taşınmasın
    setSkipQuotaEffect(false);
    setConfirmModal({ open: true, bill: bill });
  };

  const handleDeleteConfirm = () => {
    if (confirmModal.bill) {
      deleteBill({
        id: confirmModal.bill._id,
        // Kota etkisini atlama — sadece bills.deleteWithoutQuota izniyle
        // gönderilir (server ayrıca doğrular)
        ...(skipQuotaEffect && hasPermission("bills.deleteWithoutQuota")
          ? { skipQuotaEffect: true }
          : {}),
        token: auth.accessToken,
      });
      setConfirmModal({ open: false, bill: null });
    }
  };

  const handleQuotaSubmit = (formData) => {
    const mutation = modal.type === "upload" ? giveQuota : takebackQuota;
    // İşlem drawer'da seçilen firmaya gider (seçim yoksa firmanın kendisi).
    mutation({
      ...formData,
      note: formData.note || "",
      token: auth.accessToken,
      id: quotaTargetFirmId || id,
    });
    setModal({ open: false, type: "" });
  };


  const extractNote = (bill) => {
    if (!bill?.shippingInfo) return "";
    const info = bill.shippingInfo.trim();
    // Prefix'ler not girilmeden de oluşabiliyor ("Bedelsiz Yükleme -" gibi
    // tire sonrası boş) — boşluk varyasyonlarından bağımsız soy, kalan boşsa
    // "" döndür ki listede çıplak prefix görünmesin.
    //
    // KOTA PAYLAŞIMI SOYULMAZ (kullanıcı kararı 2026-08-28): açıklaması
    // "<firma> firmasına/firmasından kota transferi." cümlesiyle başlar ve bu
    // cümle bilginin kendisidir — kullanıcının notu ondan SONRA gelir. Aşağıdaki
    // `return info` ile tamamı gösterilir.
    const prefixes = ["Bedelsiz Yükleme", "İade Alma"];
    for (const p of prefixes) {
      if (info.startsWith(p)) {
        return info.slice(p.length).replace(/^[\s-]+/, "").trim();
      }
    }
    if (bill.billType === "Bill") return "";
    return info;
  };

  const isDataLoading = billsLoading || billsFetching || giveLoading || takebackLoading || deleteLoading;
  const isQuotaLoading =
    productsLoading ||
    productsFetching ||
    giveLoading ||
    takebackLoading ||
    deleteLoading;

  return (
    <div className="flex flex-col h-full w-full">
      {/* Bildirimler */}
      <Snackbar open={notification.open} autoHideDuration={4000} onClose={handleNotificationClose} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert onClose={handleNotificationClose} severity={notification.severity} sx={{ width: "100%", borderRadius: "12px", fontSize: "13px" }}>
          {notification.message}
        </Alert>
      </Snackbar>

      {/* Sticky Header */}
      <div className="sticky top-0 z-30 bg-white border-b border-gray-200 shrink-0 px-3 sm:px-6 py-3 space-y-3">
        {/* Row 1: Başlık + Butonlar */}
        <div className="flex items-center justify-between gap-3">
          {/* Başlık */}
          <div className="flex items-center gap-3 shrink-0">
            <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center shrink-0">
              <FaFileInvoice className="w-4 h-4 text-indigo-500" />
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-bold text-gray-800">Faturalar</h2>
            </div>
          </div>

          {/* Ayrı arama inputları — sağa dayalı (cihazlar sekmesiyle aynı) */}
          <div className="flex items-center gap-2.5 ml-auto shrink-0">
            {/* Alt firma süzgeci — yalnızca alt firma varsa; varsayılan
                firmanın kendisi. Seçim hem fatura listesini hem üstteki ürün
                kota toplamlarını kapsar. */}
            {hasSubFirms && (
              <div className="w-40 sm:w-52">
                <FirmDropdown
                  compact
                  multiple
                  firms={firmOptions}
                  value={selectedFirms}
                  placeholder="Firma seçin"
                  onChange={onScopeChange}
                />
              </div>
            )}
            <input
              type="text"
              placeholder="Fatura No"
              value={searchBillNo}
              onChange={(e) => setSearchBillNo(e.target.value)}
              className="w-28 sm:w-36 md:w-44 px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
            <input
              type="text"
              placeholder="Ürün Adı"
              value={searchProductName}
              onChange={(e) => setSearchProductName(e.target.value)}
              className="w-28 sm:w-36 md:w-44 px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
        </div>

        {/* Row 2: Ürün Kota Kartları (sol) + Aksiyon Butonları (sağ) — cihazlar sekmesiyle aynı */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          {/* Ürün kota bilgisi — birden fazla firma seçiliyken her kart
              "ana firma | alt firmalar" olarak ikiye ayrılır. */}
          <div className="flex items-center gap-2 overflow-x-auto no-scrollbar flex-1 min-w-0 -mx-1 px-1">
            {isQuotaLoading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="shrink-0 flex items-center gap-2.5 bg-gray-50 rounded-xl px-4 py-2.5 animate-pulse">
                  <div className="w-1.5 h-10 bg-gray-200 rounded-full" />
                  <div>
                    <div className="h-3 w-16 bg-gray-200 rounded mb-1.5" />
                    <div className="h-5 w-20 bg-gray-200 rounded" />
                  </div>
                </div>
              ))
            ) : quotaCards?.length > 0 ? (
              quotaCards.map((product, index) => {
                const color = quotaColors[index % quotaColors.length];
                return (
                  // key ürün adı: sunucu ürün adına göre TOPLAM döndürüyor.
                  <div key={product.productName} className={`shrink-0 flex items-center gap-2.5 ${color.bg} rounded-xl px-4 py-2.5 transition-all hover:shadow-sm`}>
                    <div className={`w-1.5 h-10 ${color.dot} rounded-full`} />
                    <div>
                      <p className={`text-xs font-bold ${color.label} whitespace-nowrap leading-none mb-1.5`}>{product.productName}</p>
                      {showFirmColumn ? (
                        // Birden fazla firma seçili: solda ANA FİRMA kotası,
                        // dik çizgiden sonra ALT FİRMALAR toplamı.
                        <div className="flex items-stretch gap-2.5">
                          <div className="flex flex-col leading-none">
                            <span className={`text-lg font-Bold ${color.text} tabular-nums`}>
                              {product.own.toLocaleString("tr-TR")}
                            </span>
                            <span className="text-[9px] text-gray-400 mt-1 whitespace-nowrap">
                              Ana firma
                            </span>
                          </div>
                          <span className={`w-px self-stretch ${color.dot} opacity-40`} />
                          <div className="flex flex-col leading-none">
                            <span className={`text-lg font-Bold ${color.text} tabular-nums`}>
                              {product.sub.toLocaleString("tr-TR")}
                            </span>
                            <span className="text-[9px] text-gray-400 mt-1 whitespace-nowrap">
                              Alt firmalar
                            </span>
                          </div>
                        </div>
                      ) : (
                        <p className={`text-lg font-Bold ${color.text} leading-none tabular-nums`}>
                          {product.total.toLocaleString("tr-TR")}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })
            ) : null}
          </div>

          {/* Aksiyon butonları — sağa dayalı, cihazlar sekmesiyle aynı boyut */}
          {!readOnly && hasPermission("devices.quota") && (
            <div className="flex gap-2 shrink-0 sm:ml-auto overflow-x-auto no-scrollbar -mx-1 px-1 sm:mx-0 sm:px-0 sm:flex-wrap">
              <button
                onClick={() => {
                  setQuotaTargetFirmId(id);
                  setModal({ open: true, type: "upload" });
                }}
                className="flex items-center justify-center gap-2 px-5 py-2.5 bg-emerald-400 text-white rounded-full hover:bg-emerald-500 active:scale-[0.97] text-sm font-semibold transition-all shrink-0 whitespace-nowrap"
              >
                <FaPlus className="w-3 h-3 shrink-0" />
                Bedelsiz Kota Yükle
              </button>
              <button
                onClick={() => {
                  setQuotaTargetFirmId(id);
                  setModal({ open: true, type: "return" });
                }}
                className="flex items-center justify-center gap-2 px-5 py-2.5 bg-amber-400 text-white rounded-full hover:bg-amber-500 active:scale-[0.97] text-sm font-semibold transition-all shrink-0 whitespace-nowrap"
              >
                <FaMinus className="w-3 h-3 shrink-0" />
                İade Kota Al
              </button>
            </div>
          )}
        </div>

      </div>

      {/* Scrollable Content */}
      <div className="flex-1 min-h-0 overflow-y-auto">

        {/* İçerik */}
        {isDataLoading ? (
          <div className="relative">
            <MascotLoadingOverlay message="Faturalar yükleniyor..." />
            {/* Mobil skeleton */}
            <div className="sm:hidden divide-y divide-gray-100">
              {Array.from({ length: 10 }).map((_, i) => (
                <div key={i} className="flex items-center gap-2.5 px-3 py-3 animate-pulse">
                  <div className="h-5 w-14 bg-gray-200 rounded-lg shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="h-3.5 w-3/4 bg-gray-200 rounded mb-1" />
                    <div className="h-3 w-1/2 bg-gray-100 rounded" />
                  </div>
                  <div className="shrink-0">
                    <div className="h-3.5 w-12 bg-gray-200 rounded mb-1" />
                    <div className="h-3 w-16 bg-gray-100 rounded" />
                  </div>
                </div>
              ))}
            </div>
            {/* Desktop skeleton */}
            <div className="hidden sm:block">
              <table className="w-full">
                <thead className="bg-gray-50/80">
                  <tr>
                    {["İşlem Türü", "Fatura No", "Ürün & Miktar", "Açıklama", "İşlemi Yapan", "Tutar", "Tarih", ""].map((h, i) => (
                      <th key={i} className={`px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200 ${i === 7 ? "text-center w-8" : "text-left"}`}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {Array.from({ length: 12 }).map((_, i) => (
                    <tr key={i} className="animate-pulse">
                      <td className="px-3 py-2"><div className="h-4 w-20 bg-gray-200 rounded-md" /></td>
                      <td className="px-3 py-2"><div className="h-3 w-14 bg-gray-100 rounded" /></td>
                      <td className="px-3 py-2"><div className="h-3 w-32 bg-gray-200 rounded" /></td>
                      <td className="px-3 py-2"><div className="h-3 w-20 bg-gray-100 rounded" /></td>
                      <td className="px-3 py-2"><div className="h-3 w-24 bg-gray-100 rounded" /></td>
                      <td className="px-3 py-2"><div className="h-3 w-16 bg-gray-200 rounded" /></td>
                      <td className="px-3 py-2"><div className="h-3 w-20 bg-gray-100 rounded" /></td>
                      <td className="px-3 py-2 text-center"><div className="h-3 w-3 bg-gray-100 rounded mx-auto" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : paginatedBills.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center py-12">
            <FaFileInvoice className="w-8 h-8 text-gray-300 mb-2" />
            <p className="text-sm font-semibold text-gray-600">Sonuç Bulunamadı</p>
            <p className="text-xs text-gray-400">Arama kriterlerinize uygun işlem yok.</p>
          </div>
        ) : (
          <>
            {/* Mobil: Satır bazlı liste */}
            <div className="sm:hidden divide-y divide-gray-100">
              {paginatedBills.map((group) => {
                const bill = group.lines[0];
                const single = group.lines.length === 1;
                const details = getBillTypeDetails(group.billType, group.quotaShare);
                return (
                  <div
                    key={group.key}
                    onClick={() => setDetailDrawer({ open: true, bill: group })}
                    className="flex items-center gap-2.5 px-3 py-3 cursor-pointer active:bg-gray-50"
                  >
                    <span className={`shrink-0 inline-flex items-center px-2 py-1 rounded-lg text-xs font-semibold border ${details.color}`}>
                      {details.short}
                    </span>
                    <div className="flex-1 min-w-0">
                      {/* Ürün tipi + toplam; çok tipli faturada her tip ayrı satır */}
                      {group.productSummary.map((p) => (
                        <p key={p.name} className="text-sm font-semibold text-gray-800 truncate">
                          {p.name}{" "}
                          <span className="text-gray-400">
                            × <span className="font-Bold text-gray-700 tabular-nums">{p.quota.toLocaleString("tr-TR")}</span>
                          </span>
                        </p>
                      ))}
                      <p className="text-xs text-gray-400 truncate">
                        {group.billerName} · {group.billNo}
                        {!single && ` · ${group.lines.length} kalem`}
                      </p>
                    </div>
                    <div className="shrink-0 text-right">
                      {group.billType === "Bill" && group.totalIncome ? (
                        <p className="text-sm font-Bold text-emerald-600 tabular-nums">₺{group.totalIncome.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}</p>
                      ) : (
                        <p className="text-xs text-gray-400">{details.short}</p>
                      )}
                      <p className="text-xs text-gray-400">{FormatDateHourString(group.createdInfo)}</p>
                    </div>
                    {/* Silme kalem bazlı — çok kalemli grupta drawer'dan yapılır */}
                    {single && !readOnly && hasPermission("bills.delete") && (
                      <button onClick={(e) => { e.stopPropagation(); handleDeleteRequest(bill); }} className="shrink-0 p-1.5 text-red-300 hover:text-red-600 transition-colors">
                        <FaTrashAlt className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Desktop: Kompakt tablo */}
            <div className="hidden sm:flex sm:flex-col sm:h-full overflow-hidden">
              {(() => {
                const showDelete = !readOnly && hasPermission("bills.delete");
                const headers = [
                  // Birden fazla firma seçiliyse İŞLEM FİRMASI en başta:
                  // satırın hangi firmaya ait olduğu ilk okunan bilgi olmalı.
                  ...(showFirmColumn ? ["İşlem Firması"] : []),
                  "İşlem Türü",
                  "Fatura No",
                  "Ürün & Miktar",
                  "Açıklama",
                  "İşlemi Yapan",
                  "Tutar",
                  "Tarih",
                  ...(showDelete ? [""] : []),
                ];
                return (
                  <div className="flex-1 overflow-y-auto min-h-0">
                    <table className="w-full">
                      <thead className="sticky top-0 bg-gray-50/95 backdrop-blur z-10">
                        <tr>
                          {headers.map((h, i) => (
                            <th
                              key={i}
                              className={`px-3 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-200 text-center ${i === headers.length - 1 && showDelete ? "w-10" : ""}`}
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {paginatedBills.map((group) => {
                          // Grup tek kalemden oluşuyorsa o kalemin kendisi;
                          // silme/not gibi kalem bazlı alanlar oradan okunur.
                          const bill = group.lines[0];
                          const single = group.lines.length === 1;
                          const details = getBillTypeDetails(group.billType, group.quotaShare);
                          const noteText = single ? extractNote(bill) : "";
                          return (
                            <tr
                              key={group.key}
                              onClick={() => setDetailDrawer({ open: true, bill: group })}
                              className="hover:bg-gray-50/50 transition-colors cursor-pointer"
                            >
                              {/* İşlem Firması — yalnızca birden fazla firma
                                  seçiliyken. Alt firma satırı mor yazılır. */}
                              {showFirmColumn && (
                                <td className="px-3 py-3 text-center">
                                  <span
                                    className={`text-sm truncate inline-block max-w-[180px] align-middle ${
                                      group.isSubFirmBill
                                        ? "text-violet-700 font-SemiBold"
                                        : "text-gray-600"
                                    }`}
                                    title={group.firmName}
                                  >
                                    {group.firmName || "-"}
                                  </span>
                                </td>
                              )}
                              <td className="px-3 py-3 text-center">
                                <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold border ${details.color}`}>
                                  {details.title}
                                </span>
                              </td>
                              <td className="px-3 py-3 text-center">
                                <span className="text-sm font-medium text-gray-700">{group.billNo}</span>
                                {!single && (
                                  <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-indigo-50 text-indigo-600">
                                    {group.lines.length} kalem
                                  </span>
                                )}
                              </td>
                              {/* ÜRÜN TİPİ + TOPLAM — kalemler tipe göre toplanır
                                  ("Türk Kahvesi × 2000"). Ayrıntı drawer'da. */}
                              <td className="px-3 py-3 text-center">
                                <div className="inline-flex flex-col gap-0.5">
                                  {group.productSummary.map((p) => (
                                    <span key={p.name} className="whitespace-nowrap">
                                      <span className="text-sm font-semibold text-gray-800">{p.name}</span>
                                      <span className="text-sm text-gray-400 ml-1">
                                        × <span className="font-Bold text-gray-700 tabular-nums">{p.quota.toLocaleString("tr-TR")}</span>
                                      </span>
                                    </span>
                                  ))}
                                </div>
                              </td>
                              <td className="px-3 py-3 text-center">
                                {noteText ? (
                                  <span className="text-sm text-gray-600 italic">{noteText}</span>
                                ) : (
                                  <span className="text-sm text-gray-300">—</span>
                                )}
                              </td>
                              <td className="px-3 py-3 text-center">
                                <span className="text-sm text-gray-600">{group.billerName}</span>
                              </td>
                              <td className="px-3 py-3 text-center">
                                {group.billType === "Bill" ? (
                                  <div>
                                    <span className="text-sm font-Bold text-gray-800 tabular-nums">
                                      {group.totalIncome ? `₺${group.totalIncome.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}` : "-"}
                                    </span>
                                    {/* Birim fiyat yalnizca TEK kalemli faturada
                                        anlamli; grupta kalemler farkli birim
                                        fiyatta olabilir. */}
                                    {single && bill.price ? (
                                      <p className="text-xs text-gray-400">
                                        Birim: ₺{Number(bill.price).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                                      </p>
                                    ) : null}
                                  </div>
                                ) : (
                                  <span className="text-sm text-gray-400 italic">{details.short}</span>
                                )}
                              </td>
                              <td className="px-3 py-3 text-center">
                                <span className="text-sm text-gray-500">{FormatDateHourString(group.createdInfo)}</span>
                              </td>
                              {showDelete && (
                                <td className="px-3 py-2.5 text-center w-10">
                                  {/* Silme KALEM bazlıdır. Çok kalemli grupta hangi
                                      kalemin silineceği belirsiz olduğu için buton
                                      gizlenir; silme drawer'daki kalemden yapılır. */}
                                  {single && (
                                    <button onClick={(e) => { e.stopPropagation(); handleDeleteRequest(bill); }} className="p-1.5 text-red-300 hover:text-red-600 hover:bg-red-50 rounded transition-colors">
                                      <FaTrashAlt className="w-3 h-3" />
                                    </button>
                                  )}
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              })()}
            </div>

          </>
        )}
      </div>

      {/* Sticky Pagination */}
      {totalGroupCount > pageSize && !isDataLoading && (
        <div className="shrink-0 border-t border-gray-200 bg-white/95 backdrop-blur-sm px-2 sm:px-4 py-1.5">
          <CustomPagination currentPage={pagination} totalCount={totalGroupCount} pageSize={pageSize} onPageChange={setPagination} />
        </div>
      )}

      {/* Bedelsiz Yükleme / İade Kota — Faturalar ile aynı drawer (QuotaOperationDrawer) */}
      <QuotaOperationDrawer
        open={modal.open}
        onClose={() => setModal({ open: false, type: "" })}
        firms={showFirmColumn ? selectedFirms : []}
        selectedFirmId={quotaTargetFirmId || id}
        onFirmChange={(firmId) => setQuotaTargetFirmId(firmId || id)}
        onSubmit={handleQuotaSubmit}
        type={modal.type}
        products={ownProductDatas}
        productInfoes={ownProductDatas}
        quotaArray={[]}
        devices={[]}
        isLoading={isProcessing}
      />
      {/* Fatura detay drawer'ı — satıra tıklayınca açılır */}
      <BillDetailDrawer
        open={detailDrawer.open}
        onClose={() => setDetailDrawer({ open: false, bill: null })}
        bill={detailDrawer.bill}
        typeDetails={detailDrawer.bill ? getBillTypeDetails(detailDrawer.bill.billType, detailDrawer.bill.quotaShare) : null}
        note={detailDrawer.bill ? extractNote(detailDrawer.bill) : ""}
      />

      <ConfirmationModal
        open={confirmModal.open}
        onClose={() => setConfirmModal({ open: false, bill: null })}
        onConfirm={handleDeleteConfirm}
        title="Faturayı Sil"
        message={`"${confirmModal.bill?.billNo}" numaralı faturayı silmek istediğinize emin misiniz?`}
        confirmText="Sil"
        isLoading={deleteLoading}
      >
        {hasPermission("bills.deleteWithoutQuota") && (
          <label className="flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={skipQuotaEffect}
              onChange={(e) => setSkipQuotaEffect(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-amber-600 shrink-0"
            />
            <span className="text-sm text-amber-900 leading-snug">
              <span className="font-semibold">Firma kotası etkilenmesin.</span>{" "}
              Fatura silinir ama firmanın ürün kotası geri alınmaz/değiştirilmez.
            </span>
          </label>
        )}
      </ConfirmationModal>
    </div>
  );
};

export default BillsTab;
